/**
 * Runtime validation for TotalConnect legacy WS payloads.
 * Only `type` is validated here. Per-event narrow parsing lives beside this module —
 * e.g. `wsIncomingCallLikePayload`, `wsIncomingGroupCallPayload`, `wsCallLifecyclePayload`,
 * `wsCallTransferPayload`, `wsPickupGroupPayload`, `wsCallMissedPayload`, `wsCallSessionExtrasPayload`.
 */

export type WsInboundEnvelope = Readonly<
  Record<string, unknown> & { type: string }
>;

export function parseWsInboundEnvelope(raw: unknown): WsInboundEnvelope | null {
  if (raw === null || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.type !== "string") return null;
  return o as WsInboundEnvelope;
}

/** Narrow `window` for TotalConnect-native / notification glue (FCM, cold start). */
export type TotalConnectRtcCallWindowExtension = Window & {
  __declinedCallIds?: Set<string>;
  __handledCallIds?: Set<string>;
  __pendingCallAnswer?: {
    callId?: string;
    call_id?: string;
    autoAnswer?: boolean;
  } | null;
  __pendingGroupCallData?: Record<string, unknown>;
};

/**
 * Remember declined IDs before WS / foreground envelopes arrive (FCM Decline, Header handler).
 */
export function addDeclinedIncomingCallIds(
  ids: ReadonlyArray<string | undefined | null>,
): void {
  if (typeof window === "undefined") return;
  const w = window as TotalConnectRtcCallWindowExtension;
  if (!w.__declinedCallIds) w.__declinedCallIds = new Set<string>();
  for (const id of ids) {
    if (id) w.__declinedCallIds.add(id);
  }
}

/**
 * If any id matches a notification decline, remove those ids from the set and return true.
 * Used to suppress ringing when user declined from FCM before/without local ringing state.
 */
export function consumeDeclinedIncomingIfMatched(
  ids: ReadonlyArray<string | undefined | null>,
): boolean {
  if (typeof window === "undefined") return false;
  const w = window as TotalConnectRtcCallWindowExtension;
  const declined = w.__declinedCallIds;
  if (!declined?.size) return false;
  const compact = ids.filter((x): x is string => Boolean(x));
  const hit = compact.some((id) => declined.has(id));
  if (!hit) return false;
  for (const id of compact) declined.delete(id);
  return true;
}
